import React, { useState, useEffect } from "react";
import { Sun, Briefcase, Waves, Compass, Calendar, X, MessageSquare } from "lucide-react";

// Detect mobile so we choose Drawer vs Dialog. Uses `matchMedia`.
function useIsMobile(breakpointPx = 768) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [breakpointPx]);
  return isMobile;
}

function ScheduleBody({ days }) {
  return (
    <div className="space-y-6">
      {days.map((day, di) => {
        const Icon = [Sun, Briefcase, Waves, Compass][di % 4];
        return (
          <article
            key={day.label}
            data-testid={`schedule-modal-day-${di + 1}`}
            className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 md:p-6"
          >
            <div className="flex items-start justify-between gap-4 mb-5 pb-4 border-b border-white/10">
              <div className="min-w-0">
                <div
                  className="text-[10px] font-black uppercase tracking-[0.3em] mb-1.5"
                  style={{ color: day.accent }}
                >
                  {day.tag}
                </div>
                <div className="font-display text-3xl md:text-4xl text-white leading-none">
                  {day.label}
                </div>
                <div className="text-xs md:text-sm text-white/60 mt-1.5 uppercase tracking-wide font-display-tight">
                  {day.date}
                </div>
              </div>
              <div
                className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${day.accent}22`, color: day.accent }}
              >
                <Icon size={20} />
              </div>
            </div>

            <p className="font-display-tight text-base md:text-lg text-white/90 mb-5 leading-tight">
              {day.headline}
            </p>

            <ol className="space-y-4">
              {day.sessions.map((s) => (
                <li key={s.title} className="flex gap-3 md:gap-4">
                  <div
                    className="shrink-0 w-1 self-stretch rounded-full"
                    style={{ backgroundColor: `${day.accent}55` }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-1">
                      <span
                        className="text-[10px] font-black uppercase tracking-[0.2em]"
                        style={{ color: day.accent }}
                      >
                        {s.time}
                      </span>
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border"
                        style={{ borderColor: `${day.accent}55`, color: "#fff", backgroundColor: `${day.accent}12` }}
                      >
                        {s.kind}
                      </span>
                    </div>
                    <div className="font-display-tight text-base md:text-lg text-white leading-tight mb-1">
                      {s.title}
                    </div>
                    <p className="text-sm text-white/65 leading-relaxed">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </article>
        );
      })}

      <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-xl p-4 flex items-start gap-3">
        <div className="shrink-0 w-8 h-8 rounded-lg bg-[#FACC15] text-[#0A1128] flex items-center justify-center">
          <MessageSquare size={14} />
        </div>
        <div className="text-xs md:text-sm text-white/85 leading-relaxed">
          <span className="font-black text-[#FACC15]">Speakers, mentors & panel members are being announced weekly.</span>{" "}
          The structure is locked — the lineup is coming together.
        </div>
      </div>
    </div>
  );
}

export default function ScheduleModal({ days, trigger }) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = orig; };
    }
  }, [open]);

  return (
    <>
      <div onClick={() => setOpen(true)} data-testid="schedule-modal-trigger">
        {trigger}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="schedule-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`relative bg-[#0A1128] text-white w-full md:max-w-3xl md:mx-6 md:my-8 max-h-[90vh] overflow-hidden ${
              isMobile ? "rounded-t-3xl" : "rounded-3xl"
            } shadow-2xl border border-white/10 flex flex-col`}
            role="dialog"
            aria-modal="true"
            aria-label="Full Schedule"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-[#0A1128] border-b border-white/10 px-5 md:px-8 pt-5 pb-4 flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 mb-2">
                  <Calendar size={12} className="text-[#FACC15]" />
                  <span className="text-[10px] font-black tracking-[0.3em] text-[#FACC15] uppercase">The Schedule</span>
                </div>
                <div className="font-display text-2xl md:text-3xl leading-tight">
                  Three Nights. One Estate.
                </div>
                <div className="text-sm text-white/60 mt-1">Oct 1–4, 2026 · The Grand Hacienda Palm Beach</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="schedule-modal-close"
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors"
                aria-label="Close schedule"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto px-5 md:px-8 py-6">
              <ScheduleBody days={days} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
